import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Check, ArrowLeft, ArrowRight, MapPin, Calendar as CalIcon, User as UserIcon,
  Phone as PhoneIcon, Mail as MailIcon, CreditCard, Banknote, Car, Loader2,
} from 'lucide-react';
import { pricingData, allCorfuLocations } from '../mockData';
import { countryCodes } from '../countryCodes';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- Helpers ----
const getAllLocations = () => {
  return [...new Set(allCorfuLocations)].sort();
};

const findPriceForDestination = (destination, carType) => {
  if (!destination) return null;
  const all = [...pricingData.north, ...pricingData.central, ...pricingData.south];
  const m = all.find(d => d.destination.toLowerCase() === destination.toLowerCase());
  if (!m) return null;
  return carType === 'big' ? m.big : m.small;
};

const calcPrice = (pickup, dropoff, carType) => {
  const hubs = ["corfu airport", "corfu port", "corfu town"];
  const isPickupHub = hubs.includes((pickup || '').toLowerCase());
  const isDropHub = hubs.includes((dropoff || '').toLowerCase());
  if (isPickupHub && !isDropHub) return findPriceForDestination(dropoff, carType);
  if (isDropHub && !isPickupHub) return findPriceForDestination(pickup, carType);
  return null;
};

const STEPS = [
  { num: 1, label: 'Trip Details' },
  { num: 2, label: 'Guest Details' },
  { num: 3, label: 'Booking Info' },
  { num: 4, label: 'Confirmation' },
];

const MultiStepBooking = () => {
  const { toast } = useToast();
  const allLocations = getAllLocations();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    // Step 1
    pickupLocation: '',
    dropoffLocation: '',
    passengers: '2',
    carType: 'small',
    // Step 2
    title: 'Mr',
    firstName: '',
    lastName: '',
    email: '',
    countryCode: '+30',
    phone: '',
    // Step 3
    date: '',
    returnTrip: false,
    returnDate: '',
    flightNumber: '',
    message: '',
    // Step 4
    paymentMethod: 'cash',
  });

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const smallPrice = calcPrice(form.pickupLocation, form.dropoffLocation, 'small');
  const bigPrice = calcPrice(form.pickupLocation, form.dropoffLocation, 'big');
  const currentPrice = form.carType === 'big' ? bigPrice : smallPrice;

  const canProceed = {
    1: form.pickupLocation && form.dropoffLocation && form.passengers && form.date,
    2: form.firstName && form.lastName && form.email && form.phone,
    3: true,
    4: true,
  };

  const next = () => {
    if (!canProceed[step]) {
      toast({
        title: 'Please complete all required fields',
        variant: 'destructive',
      });
      return;
    }
    setStep(s => Math.min(s + 1, 4));
  };
  const back = () => setStep(s => Math.max(s - 1, 1));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const fullName = `${form.title} ${form.firstName} ${form.lastName}`.trim();
      const payload = {
        name: fullName,
        email: form.email,
        phone: `${form.countryCode} ${form.phone}`,
        pickup_location: form.pickupLocation,
        dropoff_location: form.dropoffLocation,
        date: form.date,
        passengers: form.passengers,
        car_type: form.carType,
        payment_method: form.paymentMethod,
        price: currentPrice || '',
        message: [
          form.flightNumber && `Flight: ${form.flightNumber}`,
          form.returnTrip && form.returnDate && `Return: ${form.returnDate}`,
          form.message,
        ].filter(Boolean).join(' | '),
      };
      await axios.post(`${API}/bookings`, payload);
      setDone(true);
    } catch (err) {
      toast({
        title: 'Booking failed',
        description: err?.response?.data?.detail || 'Please try again or contact us via WhatsApp.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setDone(false);
    setStep(1);
    setForm({
      pickupLocation: '', dropoffLocation: '', passengers: '2', carType: 'small',
      title: 'Mr', firstName: '', lastName: '', email: '', countryCode: '+30', phone: '',
      date: '', returnTrip: false, returnDate: '', flightNumber: '', message: '',
      paymentMethod: 'cash',
    });
  };

  // ---- Success state ----
  if (done) {
    return (
      <Card className="border-2 border-green-200 shadow-xl max-w-4xl mx-auto">
        <CardContent className="p-8 sm:p-12 text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
            <Check className="text-white" size={40} />
          </div>
          <h3 className="text-3xl font-bold text-gray-900 mb-3">Booking Received! 🎉</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Thank you, {form.firstName}! We've received your transfer request and will confirm shortly via WhatsApp or email.
          </p>
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-6 max-w-md mx-auto mb-6 text-left">
            <p className="text-sm text-gray-600 mb-2">📍 {form.pickupLocation} → {form.dropoffLocation}</p>
            <p className="text-sm text-gray-600 mb-2">📅 {form.date}</p>
            <p className="text-sm text-gray-600 mb-2">👥 {form.passengers} passenger(s) • {form.carType === 'big' ? 'Big Car' : 'Small Car'}</p>
            {currentPrice && <p className="text-2xl font-bold text-blue-600 mt-3">{currentPrice}</p>}
          </div>
          <Button onClick={resetForm} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-booking">
            Make Another Booking
          </Button>
        </CardContent>
      </Card>
    );
  }

  // ---- Multi-step form ----
  return (
    <Card className="border border-gray-200 shadow-lg max-w-6xl mx-auto">
      <CardContent className="p-4 sm:p-8">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {STEPS.map((s, idx) => (
              <React.Fragment key={s.num}>
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                    step >= s.num
                      ? 'bg-[#1a2b4a] text-white shadow-md'
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                    {step > s.num ? <Check size={18} /> : s.num}
                  </div>
                  <span className={`text-[10px] sm:text-xs mt-1 sm:mt-2 font-medium text-center max-w-[60px] sm:max-w-none ${
                    step >= s.num ? 'text-[#1a2b4a]' : 'text-gray-400'
                  }`}>{s.label}</span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`flex-1 h-1 mx-1 sm:mx-2 mt-[-20px] rounded-full transition-all ${
                    step > s.num ? 'bg-[#1a2b4a]' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* ===== STEP 1: Trip Details (Horizontal Layout) ===== */}
        {step === 1 && (
          <div className="space-y-5">
            {/* Horizontal Search Widget - like corfutraveller.gr */}
            <div className="bg-white rounded-2xl border border-gray-200 p-2 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2">
                {/* Pick Up / Drop Off */}
                <div className="bg-gray-50 rounded-xl p-3 hover:bg-gray-100 transition cursor-text">
                  <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
                    <MapPin size={11} /> Pick Up / Drop Off
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      value={form.pickupLocation}
                      onChange={(e) => set('pickupLocation', e.target.value)}
                      list="locations-list"
                      placeholder="Pick up..."
                      data-testid="step1-pickup"
                      className="border-0 bg-transparent p-0 h-auto text-sm focus-visible:ring-0 placeholder:text-gray-400"
                    />
                    <Input
                      value={form.dropoffLocation}
                      onChange={(e) => set('dropoffLocation', e.target.value)}
                      list="locations-list"
                      placeholder="Drop off..."
                      data-testid="step1-dropoff"
                      className="border-0 bg-transparent p-0 h-auto text-sm focus-visible:ring-0 placeholder:text-gray-400"
                    />
                  </div>
                </div>

                {/* Select Dates */}
                <div className="bg-gray-50 rounded-xl p-3 hover:bg-gray-100 transition">
                  <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
                    <CalIcon size={11} /> Select Dates
                  </div>
                  <Input
                    type="datetime-local"
                    value={form.date}
                    onChange={(e) => set('date', e.target.value)}
                    data-testid="step1-date"
                    className="border-0 bg-transparent p-0 h-auto text-sm focus-visible:ring-0 placeholder:text-gray-400"
                  />
                </div>

                {/* Select Guests */}
                <div className="bg-gray-50 rounded-xl p-3 hover:bg-gray-100 transition">
                  <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
                    <UserIcon size={11} /> Select Guests
                  </div>
                  <Input
                    type="number"
                    value={form.passengers}
                    onChange={(e) => set('passengers', e.target.value)}
                    min="1"
                    max="8"
                    placeholder="2"
                    data-testid="step1-passengers"
                    className="border-0 bg-transparent p-0 h-auto text-sm focus-visible:ring-0 placeholder:text-gray-400"
                  />
                </div>

                {/* Search Button */}
                <Button
                  type="button"
                  data-testid="step1-search"
                  className="bg-[#1a2b4a] hover:bg-[#243a5e] text-white h-auto px-8"
                >
                  🔍 Search
                </Button>
              </div>
              <datalist id="locations-list">
                {allLocations.map((loc) => <option key={loc} value={loc} />)}
              </datalist>
            </div>

            {/* Vehicle selection - Premium Cards */}
            {(smallPrice || bigPrice) && (
              <div className="mt-6">
                <div className="bg-gray-100 rounded-2xl p-3 mb-4 text-center">
                  <p className="text-sm font-bold text-[#1a2b4a]">🛎️ Availability</p>
                  <p className="text-xs text-gray-600 mt-0.5">Choose your preferred vehicle below</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Premium Sedan (Small Car) */}
                  <div
                    onClick={() => set('carType', 'small')}
                    data-testid="step1-car-small"
                    className={`p-5 rounded-2xl border-2 transition-all cursor-pointer ${
                      form.carType === 'small'
                        ? 'border-[#1a2b4a] bg-white shadow-xl ring-2 ring-[#1a2b4a]/20'
                        : 'border-gray-200 bg-white hover:border-[#1a2b4a]/40 hover:shadow-md'
                    }`}
                  >
                    <div className="h-32 sm:h-40 mb-3 rounded-xl bg-gradient-to-br from-gray-50 to-white flex items-center justify-center overflow-hidden">
                      <img
                        src="https://customer-assets.emergentagent.com/job_corfu-rides/artifacts/qd0sw8uy_mercedes-benz-cla-class-color-161839-removebg-preview.png"
                        alt="Premium Sedan"
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                    <h4 className="font-bold text-lg text-[#1a2b4a] mb-1">Premium Sedan</h4>
                    <p className="text-xs text-gray-500 mb-3">Mercedes-Benz E-Class or similar</p>
                    <div className="flex items-center gap-1 mb-3 text-sm text-gray-700">
                      <UserIcon size={14} className="text-[#1a2b4a]" />
                      <span className="font-semibold">1-4 passengers</span>
                    </div>
                    <div className="space-y-1 mb-4 text-xs text-gray-600">
                      <div className="flex items-center gap-1.5"><Check size={12} className="text-green-600" /> Leather Interior</div>
                      <div className="flex items-center gap-1.5"><Check size={12} className="text-green-600" /> Individual A/C</div>
                      <div className="flex items-center gap-1.5"><Check size={12} className="text-green-600" /> USB Ports</div>
                      <div className="flex items-center gap-1.5"><Check size={12} className="text-green-600" /> Bluetooth</div>
                    </div>
                    <div className="border-t pt-3 flex items-end justify-between">
                      <div>
                        <p className="text-xs text-gray-500">from</p>
                        <p className="text-2xl font-bold text-[#1a2b4a]">{smallPrice || 'N/A'}</p>
                      </div>
                      <div className={`px-4 py-2 rounded-lg text-sm font-bold ${
                        form.carType === 'small' ? 'bg-green-600 text-white' : 'bg-[#1a2b4a] text-white'
                      }`}>
                        {form.carType === 'small' ? '✓ Added' : 'Add'}
                      </div>
                    </div>
                  </div>

                  {/* Mercedes Minivan (Big Car) */}
                  <div
                    onClick={() => set('carType', 'big')}
                    data-testid="step1-car-big"
                    className={`p-5 rounded-2xl border-2 transition-all cursor-pointer ${
                      form.carType === 'big'
                        ? 'border-[#1a2b4a] bg-white shadow-xl ring-2 ring-[#1a2b4a]/20'
                        : 'border-gray-200 bg-white hover:border-[#1a2b4a]/40 hover:shadow-md'
                    }`}
                  >
                    <div className="h-32 sm:h-40 mb-3 rounded-xl bg-gradient-to-br from-gray-50 to-white flex items-center justify-center overflow-hidden">
                      <img
                        src="https://customer-assets.emergentagent.com/job_corfu-rides/artifacts/btrcr8i1_WhatsApp%20Image%202026-05-12%20at%2016.55.31.jpeg"
                        alt="Mercedes-Benz Minivan"
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                    <h4 className="font-bold text-lg text-[#1a2b4a] mb-1">Mercedes-Benz Minivan</h4>
                    <p className="text-xs text-gray-500 mb-3">Mercedes-Benz Vito or similar</p>
                    <div className="flex items-center gap-1 mb-3 text-sm text-gray-700">
                      <UserIcon size={14} className="text-[#1a2b4a]" />
                      <span className="font-semibold">5-8 passengers</span>
                    </div>
                    <div className="space-y-1 mb-4 text-xs text-gray-600">
                      <div className="flex items-center gap-1.5"><Check size={12} className="text-green-600" /> Leather Interior</div>
                      <div className="flex items-center gap-1.5"><Check size={12} className="text-green-600" /> Individual A/C</div>
                      <div className="flex items-center gap-1.5"><Check size={12} className="text-green-600" /> Tinted Windows</div>
                      <div className="flex items-center gap-1.5"><Check size={12} className="text-green-600" /> USB Ports</div>
                    </div>
                    <div className="border-t pt-3 flex items-end justify-between">
                      <div>
                        <p className="text-xs text-gray-500">from</p>
                        <p className="text-2xl font-bold text-[#1a2b4a]">{bigPrice || 'N/A'}</p>
                      </div>
                      <div className={`px-4 py-2 rounded-lg text-sm font-bold ${
                        form.carType === 'big' ? 'bg-green-600 text-white' : 'bg-[#1a2b4a] text-white'
                      }`}>
                        {form.carType === 'big' ? '✓ Added' : 'Add'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {form.pickupLocation && form.dropoffLocation && !smallPrice && !bigPrice && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-3 text-sm text-yellow-800 text-center">
                📍 Custom location — we'll send you a quote shortly!
              </div>
            )}

            {!form.pickupLocation && !form.dropoffLocation && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                  <h4 className="font-bold text-[#1a2b4a] mb-1">🛎️ Availability</h4>
                  <p className="text-sm text-gray-600">Please fill pick up and drop off to see prices</p>
                </div>
                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                  <h4 className="font-bold text-[#1a2b4a] mb-1">📋 Your reservation</h4>
                  <p className="text-sm text-gray-600">Your reservation list is empty</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===== STEP 2: Guest Details ===== */}
        {step === 2 && (
          <div className="space-y-5">
            <div className="text-center mb-4">
              <h3 className="text-2xl font-bold text-gray-900">Your Details</h3>
              <p className="text-sm text-gray-600 mt-1">So we know who's traveling</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
              <div className="flex gap-2">
                {['Mr', 'Ms', 'Mrs'].map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => set('title', t)}
                    data-testid={`title-${t}`}
                    className={`px-6 py-2 rounded-full border-2 transition-all ${
                      form.title === t
                        ? 'border-blue-600 bg-blue-600 text-white'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                <Input value={form.firstName} onChange={(e) => set('firstName', e.target.value)} placeholder="John" data-testid="step2-firstname" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                <Input value={form.lastName} onChange={(e) => set('lastName', e.target.value)} placeholder="Doe" data-testid="step2-lastname" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
              <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="john@example.com" data-testid="step2-email" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
              <div className="flex gap-2">
                <select
                  value={form.countryCode}
                  onChange={(e) => set('countryCode', e.target.value)}
                  data-testid="step2-country"
                  className="border-2 border-gray-200 rounded-lg px-3 py-2 bg-white text-sm w-32 focus:border-blue-500 focus:outline-none"
                >
                  {countryCodes.map((c, i) => (
                    <option key={i} value={c.code}>{c.flag} {c.code}</option>
                  ))}
                </select>
                <Input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => set('phone', e.target.value)}
                  placeholder="697 1234567"
                  className="flex-1"
                  data-testid="step2-phone"
                />
              </div>
            </div>
          </div>
        )}

        {/* ===== STEP 3: Booking Information ===== */}
        {step === 3 && (
          <div className="space-y-5">
            <div className="text-center mb-4">
              <h3 className="text-2xl font-bold text-gray-900">Additional Information</h3>
              <p className="text-sm text-gray-600 mt-1">Optional details to make your transfer perfect</p>
            </div>

            <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.returnTrip}
                  onChange={(e) => set('returnTrip', e.target.checked)}
                  className="w-5 h-5 rounded text-blue-600 focus:ring-blue-500"
                  data-testid="step3-return"
                />
                <span className="font-medium text-gray-900">I need a return transfer too</span>
              </label>
              {form.returnTrip && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Return Date & Time</label>
                  <Input
                    type="datetime-local"
                    value={form.returnDate}
                    onChange={(e) => set('returnDate', e.target.value)}
                    data-testid="step3-return-date"
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Flight / Ferry Number (optional)</label>
              <Input
                value={form.flightNumber}
                onChange={(e) => set('flightNumber', e.target.value)}
                placeholder="e.g. LH 1234"
                data-testid="step3-flight"
              />
              <p className="text-xs text-gray-500 mt-1">Helps us track your arrival in case of delays</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Special Requests (optional)</label>
              <Textarea
                value={form.message}
                onChange={(e) => set('message', e.target.value)}
                placeholder="Child seats, large luggage, accessibility needs, etc."
                rows={3}
                data-testid="step3-message"
              />
            </div>
          </div>
        )}

        {/* ===== STEP 4: Confirmation ===== */}
        {step === 4 && (
          <div className="space-y-5">
            <div className="text-center mb-4">
              <h3 className="text-2xl font-bold text-gray-900">Confirm Your Booking</h3>
              <p className="text-sm text-gray-600 mt-1">Review details and choose payment method</p>
            </div>

            {/* Summary */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-5 border border-blue-100 space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="text-blue-600 mt-0.5 flex-shrink-0" size={18} />
                <div className="text-sm">
                  <div className="font-bold text-gray-900">{form.pickupLocation}</div>
                  <div className="text-gray-500 text-xs">↓</div>
                  <div className="font-bold text-gray-900">{form.dropoffLocation}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm pt-3 border-t border-blue-100">
                <div><CalIcon size={14} className="inline mr-1 text-blue-600" /> {form.date || '—'}</div>
                <div><UserIcon size={14} className="inline mr-1 text-blue-600" /> {form.passengers} passenger(s)</div>
                <div><Car size={14} className="inline mr-1 text-blue-600" /> {form.carType === 'big' ? 'Big Car (5-8)' : 'Small Car (1-4)'}</div>
                <div><PhoneIcon size={14} className="inline mr-1 text-blue-600" /> {form.countryCode} {form.phone}</div>
              </div>
              {form.returnTrip && form.returnDate && (
                <div className="text-sm pt-2 border-t border-blue-100 text-gray-700">
                  🔄 Return on: {form.returnDate}
                </div>
              )}
              {form.flightNumber && (
                <div className="text-sm text-gray-700">✈️ {form.flightNumber}</div>
              )}
              {form.message && (
                <div className="text-sm text-gray-700">📝 {form.message}</div>
              )}
            </div>

            {/* Price */}
            {currentPrice && (
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200 rounded-2xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-amber-800">Total Price</p>
                  <p className="text-xs text-amber-700 opacity-80">All-inclusive • No hidden fees</p>
                </div>
                <p className="text-4xl font-bold text-amber-700">{currentPrice}</p>
              </div>
            )}

            {/* Payment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Payment Method *</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => set('paymentMethod', 'cash')}
                  data-testid="step4-cash"
                  className={`p-4 rounded-xl border-2 transition-all ${
                    form.paymentMethod === 'cash'
                      ? 'border-green-500 bg-green-50 shadow-lg'
                      : 'border-gray-200 bg-white hover:border-green-300'
                  }`}
                >
                  <Banknote className="mx-auto mb-2 text-green-600" size={24} />
                  <p className="font-bold text-gray-900">Pay Cash</p>
                  <p className="text-xs text-gray-500 mt-1">To driver</p>
                </button>
                <button
                  type="button"
                  onClick={() => set('paymentMethod', 'card')}
                  data-testid="step4-card"
                  className={`p-4 rounded-xl border-2 transition-all ${
                    form.paymentMethod === 'card'
                      ? 'border-blue-500 bg-blue-50 shadow-lg'
                      : 'border-gray-200 bg-white hover:border-blue-300'
                  }`}
                >
                  <CreditCard className="mx-auto mb-2 text-blue-600" size={24} />
                  <p className="font-bold text-gray-900">Pay by Card</p>
                  <p className="text-xs text-gray-500 mt-1">To driver</p>
                </button>
              </div>
              <p className="text-xs text-center text-gray-500 mt-2">
                ℹ️ Just your preference — payment is made directly to the driver
              </p>
            </div>
          </div>
        )}

        {/* ===== Navigation Buttons ===== */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={back}
            disabled={step === 1}
            data-testid="booking-back"
            className="disabled:opacity-40"
          >
            <ArrowLeft size={16} className="mr-1" /> Back
          </Button>

          <span className="text-xs text-gray-500">
            Step {step} of 4
          </span>

          {step < 4 ? (
            <Button
              type="button"
              onClick={next}
              data-testid="booking-next"
              className="bg-[#1a2b4a] hover:bg-[#243a5e] text-white"
            >
              Next <ArrowRight size={16} className="ml-1" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="booking-submit"
              className="bg-[#1a2b4a] hover:bg-[#243a5e] text-white disabled:opacity-50"
            >
              {submitting ? <><Loader2 size={16} className="mr-1 animate-spin" /> Sending...</> : <>Confirm Booking <Check size={16} className="ml-1" /></>}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default MultiStepBooking;
